<template>
  <v-card class="pa-4">
    <v-card-title class="d-flex align-center justify-space-between mb-4">
      <span class="text-h5">{{ title }}</span>
      <v-btn
        color="primary"
        @click="refreshPins"
        :loading="loading"
        variant="outlined"
      >
        <v-icon left>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
    </v-card-title>

    <v-alert
      v-if="error"
      type="error"
      class="mb-4"
      closable
      @click:close="clearError"
    >
      {{ error }}
    </v-alert>

    <!-- Tabs for Active/Expired PINs -->
    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab value="active">
        <v-icon left>mdi-check-circle</v-icon>
        PINs Activos ({{ activePins.length }})
      </v-tab>
      <v-tab value="expired">
        <v-icon left>mdi-clock-alert</v-icon>
        PINs Expirados ({{ expiredPins.length }})
      </v-tab>
    </v-tabs>

    <v-tabs-window v-model="activeTab">
      <!-- Active PINs Tab -->
      <v-tabs-window-item value="active">
        <v-data-table
          :headers="headers"
          :items="activePins"
          :loading="loading"
          class="elevation-1"
          no-data-text="No hay PINs activos"
          loading-text="Cargando PINs..."
        >
          <template #item.code="{ item }">
            <div class="font-weight-bold text-h6" style="font-family: monospace; letter-spacing: 2px;">
              {{ item.code }}
            </div>
          </template>

          <template #item.school_subject.name="{ item }">
            <v-chip
              v-if="item.school_subject?.name"
              color="primary"
              size="small"
              variant="outlined"
            >
              {{ item.school_subject.name }}
            </v-chip>
            <span v-else class="text-disabled">-</span>
          </template>

          <template #item.created_by.name="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.created_by?.name || 'N/A' }}</div>
              <v-chip
                :color="getRoleColor(item.created_by?.role)"
                size="x-small"
                variant="tonal"
                class="text-caption"
              >
                {{ getRoleText(item.created_by?.role) }}
              </v-chip>
            </div>
          </template>

          <template #item.expires_at="{ item }">
            <div>
              {{ formatDate(item.expires_at) }}
            </div>
            <v-chip
              :color="getExpirationColor(item.expires_at)"
              size="small"
              class="mt-1"
            >
              {{ getTimeRemaining(item.expires_at) }}
            </v-chip>
          </template>

          <template #item.extra_time="{ item }">
            {{ item.extra_time || 0 }} seg
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2">
              <v-btn
                color="primary"
                size="small"
                variant="outlined"
                @click="openSessionDashboard(item)"
              >
                <v-icon>mdi-monitor-dashboard</v-icon>
                Sesión
              </v-btn>
              <v-btn
                color="error"
                size="small"
                @click="confirmDelete(item)"
                :loading="deletingIds.includes(item.id)"
              >
                <v-icon>mdi-delete</v-icon>
                Eliminar
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-tabs-window-item>

      <!-- Expired PINs Tab -->
      <v-tabs-window-item value="expired">
        <v-data-table
          :headers="expiredHeaders"
          :items="expiredPins"
          :loading="loading"
          class="elevation-1"
          no-data-text="No hay PINs expirados"
          loading-text="Cargando PINs..."
        >
          <template #item.code="{ item }">
            <div class="font-weight-bold text-disabled" style="font-family: monospace; letter-spacing: 2px;">
              {{ item.code }}
            </div>
          </template>

          <template #item.school_subject.name="{ item }">
            <v-chip
              v-if="item.school_subject?.name"
              color="grey"
              size="small"
              variant="outlined"
            >
              {{ item.school_subject.name }}
            </v-chip>
            <span v-else class="text-disabled">-</span>
          </template>

          <template #item.created_by.name="{ item }">
            <div>
              <div class="font-weight-medium text-disabled">{{ item.created_by?.name || 'N/A' }}</div>
              <v-chip
                color="grey"
                size="x-small"
                variant="tonal"
                class="text-caption"
              >
                {{ getRoleText(item.created_by?.role) }}
              </v-chip>
            </div>
          </template>

          <template #item.expires_at="{ item }">
            <div class="text-disabled">
              {{ formatDate(item.expires_at) }}
            </div>
            <v-chip color="grey" size="small" class="mt-1">
              Expirado
            </v-chip>
          </template>

          <template #item.extra_time="{ item }">
            {{ item.extra_time || 0 }} seg
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2">
              <v-btn
                color="success"
                size="small"
                variant="outlined"
                @click="downloadSessionReport(item)"
                :loading="downloadingReports.includes(item.code)"
              >
                <v-icon>mdi-file-pdf-box</v-icon>
                Reporte
              </v-btn>
              <v-btn
                color="error"
                size="small"
                variant="outlined"
                @click="confirmDelete(item)"
                :loading="deletingIds.includes(item.id)"
              >
                <v-icon>mdi-delete</v-icon>
                Eliminar
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-tabs-window-item>
    </v-tabs-window>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          Confirmar eliminación
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar este PIN?
          <div v-if="pinToDelete" class="mt-3 pa-3 bg-grey-lighten-4 rounded">
            <strong>Código:</strong> {{ pinToDelete.code }}<br>
            <strong>Grupo:</strong> {{ pinToDelete.group?.name || 'N/A' }}<br>
            <strong>Asignatura:</strong> {{ pinToDelete.school_subject?.name || 'Sin asignatura' }}<br>
            <strong>Creado por:</strong> {{ pinToDelete.created_by?.name || 'N/A' }}
            <v-chip
              :color="getRoleColor(pinToDelete.created_by?.role)"
              size="x-small"
              variant="tonal"
              class="ml-2"
            >
              {{ getRoleText(pinToDelete.created_by?.role) }}
            </v-chip><br>
            <strong>Expira:</strong> {{ formatDate(pinToDelete.expires_at) }}
          </div>
          <v-alert type="warning" class="mt-3" variant="tonal">
            Esta acción no se puede deshacer. El PIN se volverá inválido inmediatamente.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteDialog = false" variant="outlined">
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            @click="deletePin"
            :loading="deletingIds.includes(pinToDelete?.id)"
          >
            Eliminar PIN
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const props = defineProps({
  title: {
    type: String,
    default: 'Gestión de PINs'
  }
})

const emit = defineEmits(['pin-deleted'])

const store = useStore()
const router = useRouter()

const activeTab = ref('active')
const deleteDialog = ref(false)
const pinToDelete = ref(null)
const deletingIds = ref([])
const downloadingReports = ref([])

const loading = computed(() => store.getters['pins/isLoading'])
const error = computed(() => store.getters['pins/getError'])
const activePins = computed(() => store.getters['pins/activePins'])
const expiredPins = computed(() => store.getters['pins/expiredPins'])

const headers = [
  { title: 'Código', value: 'code', sortable: false },
  { title: 'Grupo', value: 'group.name', sortable: true },
  { title: 'Asignatura', value: 'school_subject.name', sortable: true },
  { title: 'Creado por', value: 'created_by.name', sortable: true },
  { title: 'Expira', value: 'expires_at' },
  { title: 'Tiempo Extra', value: 'extra_time', align: 'center' },
  { title: 'Acciones', value: 'actions', sortable: false, align: 'center' }
]

const expiredHeaders = [
  { title: 'Código', value: 'code', sortable: false },
  { title: 'Grupo', value: 'group.name', sortable: true },
  { title: 'Asignatura', value: 'school_subject.name', sortable: true },
  { title: 'Creado por', value: 'created_by.name', sortable: true },
  { title: 'Expiró', value: 'expires_at' },
  { title: 'Tiempo Extra', value: 'extra_time', align: 'center' },
  { title: 'Acciones', value: 'actions', sortable: false, align: 'center' }
]

const refreshPins = async () => {
  await store.dispatch('pins/fetchPins')
}

const openSessionDashboard = (pin) => {
  router.push({
    name: 'SessionDashboard',
    params: { pinCode: pin.code }
  })
}

const confirmDelete = (pin) => {
  pinToDelete.value = pin
  deleteDialog.value = true
}

const deletePin = async () => {
  if (!pinToDelete.value) return

  deletingIds.value.push(pinToDelete.value.id)

  try {
    const result = await store.dispatch('pins/deletePin', pinToDelete.value.id)
    
    if (result.success) {
      emit('pin-deleted', pinToDelete.value)
      deleteDialog.value = false
      pinToDelete.value = null
    }
  } finally {
    deletingIds.value = deletingIds.value.filter(id => id !== pinToDelete.value?.id)
  }
}

const clearError = () => {
  store.dispatch('pins/clearError')
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getTimeRemaining = (expiresAt) => {
  const now = new Date()
  const expiry = new Date(expiresAt)
  const diff = expiry - now

  if (diff <= 0) return 'Expirado'

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 24) {
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
  }
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  
  return `${minutes}m`
}

const getExpirationColor = (expiresAt) => {
  const now = new Date()
  const expiry = new Date(expiresAt)
  const diff = expiry - now
  const hours = diff / (1000 * 60 * 60)

  if (hours <= 0) return 'error'
  if (hours <= 1) return 'warning'
  if (hours <= 24) return 'info'
  return 'success'
}

const getRoleColor = (role) => {
  switch (role) {
    case 'admin': return 'error'
    case 'teacher': return 'primary'
    case 'student': return 'success'
    default: return 'grey'
  }
}

const getRoleText = (role) => {
  switch (role) {
    case 'admin': return 'Admin'
    case 'teacher': return 'Profesor'
    case 'student': return 'Estudiante'
    default: return 'N/A'
  }
}

const downloadSessionReport = async (pin) => {
  if (!pin.code) {
    console.error('No pin code found')
    return
  }

  downloadingReports.value.push(pin.code)

  try {
    const result = await store.dispatch('reports/downloadPDF', {
      pin_code: pin.code
    })

    if (!result.success) {
      store.dispatch('reports/clearError')
      console.error('Error downloading report:', result.error)
    }
  } catch (error) {
    console.error('Error downloading report:', error)
  } finally {
    downloadingReports.value = downloadingReports.value.filter(
      code => code !== pin.code
    )
  }
}

onMounted(() => {
  refreshPins()
})
</script>

<style scoped>
.v-data-table :deep(.font-weight-bold) {
  font-family: 'Courier New', monospace;
}

.text-disabled {
  opacity: 0.6;
}
</style>